import { useQuery, useMutation } from 'react-query'
import { ApiClient } from '@/lib/api'
import { useState, createContext, ReactNode, useMemo, useContext } from 'react';

// API Clients
const apiClientMap: Record<string, ApiClient> = {
	"mainnet": new ApiClient(undefined, "mainnet"),
	"testnet": new ApiClient(undefined, "testnet")
}

interface ApiConfig {
	network: string;
	setNetwork: (v: string) => void;
}

const ApiConfigContext = createContext<ApiConfig|undefined>(undefined);

export const ApiConfigProvider = ({ children }: { children: ReactNode }) => {
	const [network, setNetwork] = useState<string>("mainnet");

	const val = useMemo(() => {
		return {
			network, 
			setNetwork
		}
	}, [network])

	return (
		<ApiConfigContext.Provider value={val}>
			{ children }
		</ApiConfigContext.Provider>
	);
};

export const useApiConfig = () => {
	const ctx = useContext(ApiConfigContext);
	if (!ctx) throw new Error("API Config is only accessible within an ApiConfigProvider");
	return ctx;
}

// Network
export function useNetworkStats() {
	const { network } = useApiConfig()

	return useQuery<any>({
		queryFn: () => apiClientMap[network].getNetworkStats(),
		queryKey: ['networkStats', network],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}

export function useNetworkBlobSizeDistribution() {
	const { network } = useApiConfig()

	return useQuery<any>({
		queryFn: () => apiClientMap[network].getNetworkBlobSizeDistribution(),
		queryKey: ['networkBlobSizeDistribution', network],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}

export function useNetworkDataGrowthRate() {
	const { network } = useApiConfig()

	return useQuery<any>({
		queryFn: () => apiClientMap[network].getNetworkDataGrowthRate(),
		queryKey: ['networkDataGrowthRate', network],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}

export function useNetworkBlobGrowthRate() {
	const { network } = useApiConfig()

	return useQuery<any>({
		queryFn: () => apiClientMap[network].getNetworkBlobGrowthRate(),
		queryKey: ['networkBlobGrowthRate', network],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}


// Operators
export function useOperators() {
	const { network } = useApiConfig()
	console.log(network);

	return useQuery<any>({
		queryFn: () => apiClientMap[network].getOperators(),
		queryKey: ['operators', network],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}

// Blobs
export function useBlobs(limit?: number, txDigest?: string, eventSeq?: string) {
	const { network } = useApiConfig()

	return useQuery<any>({
		queryFn: () => apiClientMap[network].getBlobs(limit, txDigest, eventSeq),
		queryKey: ['blobs', txDigest, eventSeq, network],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}

export function useBlob(id: string) {
	const { network } = useApiConfig()

	return useQuery<any>({
		queryFn: () => apiClientMap[network].getBlob(id),
		queryKey: ['blob', id, network],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}

export function useBlobVersions(id: string) {
	const { network } = useApiConfig()

	return useQuery<any>({
		queryFn: () => apiClientMap[network].getBlobVersions(id),
		queryKey: ['blobVersions', id, network],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}

// Events
export function useEvents(limit?: number, txDigest?: string, eventSeq?: string) {
	const { network } = useApiConfig()

	return useQuery<any>({
		queryFn: () => apiClientMap[network].getEvents(limit, txDigest, eventSeq),
		queryKey: ['events', txDigest, eventSeq, network],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}

// Search
export function useSearch() {
	const { network } = useApiConfig();

	return useMutation({
		mutationFn: (searchInput: string) => apiClientMap[network].getSearch(searchInput)
	});
}
